import { Component } from 'preact';
import { createPortal } from 'preact/compat';
import GoogleMapReact from 'google-map-react';
import Supercluster from 'supercluster';
import MapMarker from "./MapMarker";
import ClusterMarker from "./ClusterMarker";
import {
    GOOGLE_API_KEY,
    GOOGLE_MAPS_API_KEY_URL,
    MAP_IDLE_TIMEOUT_MS,
    METER_IN_DEGREE,
    TOOLTIP_TOP_MARGIN_IN_PIXEL
} from "../../../constants/constants";
import { doGet } from "../../../util/NetworkUtils";
import MapControls from "./MapControls";

const MAX_CLUSTER_ZOOM = 13;

export default class PickupLocationMapForm extends Component {

    timerId = null;
    abortController = null;
    centering = false;
    keyUp = false;

    static defaultProps = {
        open: false,
        searchByLocation: () => {}
    };

    constructor(props) {
        super(props);

        // Current Paazl coordinates, only used as a fallback option
        let initialCenter = {lat: 52.34108529766617, lng: 4.823682292015647};

        if (props.pickupLocations && props.pickupLocations.length > 0) {
            const singleLocation = props.pickupLocations[0];
            if (singleLocation.coordinates && singleLocation.coordinates.latitude && singleLocation.coordinates.longitude) {
                initialCenter = {
                    lat: singleLocation.coordinates.latitude,
                    lng: singleLocation.coordinates.longitude
                };
            }
        }

        this.state = {
            googleMapsApiKey: null,
            mapLoaded: false,
            zoom: 15,
            size: {
                width: 700,
                height: 500
            },
            center: initialCenter,
            clusterIndex: null,
            currentMapProps: null,
            animationPaused: false
        };
    }

    componentDidMount() {
        const {config} = this.props;

        if (!window.PAAZL_CHECKOUT_WIDGET_API_URL) {
            doGet(GOOGLE_MAPS_API_KEY_URL, config)
                .then(response => {
                    this.setState({googleMapsApiKey: response.data});
                })
                .catch(error => {
                    console.error(error);
                    this.setState({googleMapsApiKey: GOOGLE_API_KEY});
                });
        } else {
            this.setState({googleMapsApiKey: GOOGLE_API_KEY});
        }

        window.addEventListener("resize", this.windowResizeEventListener);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.windowResizeEventListener);
        this.abortFetchLocations();
    }

    componentDidUpdate(previousProps, previousState) {
        const {pickupLocations} = this.props;

        if (JSON.stringify(previousProps.pickupLocations) !== JSON.stringify(pickupLocations)) {
            this.buildClusterIndex();
            this.updateMapPosition(pickupLocations);
        }

        if (previousState.zoom !== this.state.zoom) {
            this.updateMapPosition(pickupLocations);
        }
    }

    onMapLoaded = () => {
        this.setState({mapLoaded: true});
    };

    // Make a container div to host our Map Controls in the Google Maps UI
    onGoogleApiLoaded = ({map, maps}) => {
        this.mapControlsContainer = document.createElement('div');
        map.controls[maps.ControlPosition.TOP_LEFT].push(this.mapControlsContainer);
    };
    
    setTooltipRef = (node) => {
        this.tooltipRef = node;
    };

    windowResizeEventListener = () => this.updateMapPosition();

    updateMapPosition = (pickupLocations) => {
        const {open, pickupLocations: propsPickupLocations} = this.props;
        const {center: oldCenter, zoom} = this.state;

        if (!pickupLocations) {
            pickupLocations = propsPickupLocations;
        }

        if (open && !this.fetchLocationsScheduled()) {
            const currentPoint = pickupLocations && pickupLocations.find(pickupLocation => pickupLocation.checked);
            const center = currentPoint ? this.calculateMapCenter(currentPoint, zoom) : oldCenter;

            if (!this.isSameCoordinates(oldCenter, center)) {
                this.centering = !!currentPoint;

                this.setState({
                    center
                });
            }
        }
    };

    onTogglePause = (isPaused) => {
        this.setState({animationPaused: isPaused});
    };

    equalsLists = (primary, secondary) => {
        if (!primary || !secondary || primary.length !== secondary.length) {
            return false;
        }

        for (let i = 0; i < primary.length; i++) {
            if (primary[i].code !== secondary[i].code) {
                return false;
            }
        }

        return true;
    };

    calculateMapCenter = (pickupLocation, zoom) => {
        if (pickupLocation && pickupLocation.coordinates) {
            return {
                lat: pickupLocation.coordinates.latitude + this.calculateMarginTopDistance(zoom, pickupLocation.coordinates.latitude),
                lng: pickupLocation.coordinates.longitude
            };
        }
    };

    calculateMarginTopDistance = (zoom, latitude) => {
        if (zoom === -1 || !this.tooltipRef) {
            return 0;
        }

        const zoomMeters = this.calculateMetersPerPx(latitude, zoom);
        return METER_IN_DEGREE * zoomMeters * ((TOOLTIP_TOP_MARGIN_IN_PIXEL + this.tooltipRef.clientHeight) - (this.state.size.height / 2));
    };

    /**
     * Converts zoom level to real-world scale at a given latitude.
     * Based on Google Maps API documentation (Web Mercator)
     */
    calculateMetersPerPx(latitude, zoom) {
        return 156543.03392 * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom);
    }

    onMapChange = (mapProps) => {
        const {zoom, size, center, bounds} = mapProps;
        const {zoom: oldZoom, size: oldSize, center: oldCenter} = this.state;

        this.abortFetchLocations();

        this.setState({currentMapProps: mapProps});

        const southWest = () => {
            return {
                latitude: bounds && bounds.sw.lat,
                longitude: bounds && bounds.sw.lng
            };
        };
        const northEast = () => {
            return {
                latitude: bounds && bounds.ne.lat,
                longitude: bounds && bounds.ne.lng
            };
        };
        const newCenter = {latitude: center.lat, longitude: center.lng};

        const zoomChanged = zoom !== 0 && zoom !== oldZoom;
        const centerChanged = !this.isSameCoordinates(oldCenter, center);
        const sizeChanged = oldSize.width !== size.width || oldSize.height !== size.height;

        if (zoomChanged && !this.keyUp) {
            this.scheduleFetchLocations(southWest(), northEast(), newCenter);
        }

        if (centerChanged && !this.keyUp && !this.centering) {
            this.scheduleFetchLocations(southWest(), northEast(), newCenter);
        }

        if (zoomChanged || sizeChanged || centerChanged) {
            if (this.keyUp) {
                this.scheduleFetchLocations(southWest(), northEast(), newCenter);
            }

            this.setState({
                zoom,
                size,
                center
            });
        }

        if (this.keyUp) {
            this.keyUp = false;
        }

        if (this.centering) {
            this.centering = false;
        }
    };

    buildClusterIndex() {
        const {pickupLocations} = this.props;
        const {zoom} = this.state;

        if (!pickupLocations?.length || zoom > MAX_CLUSTER_ZOOM) {
            this.setState({clusterIndex: null});
            return;
        }

        const points = this.pickupLocationsToGeoJSONFeatures(pickupLocations);

        const clusterIndex = new Supercluster({
            radius: 150,
            maxZoom: MAX_CLUSTER_ZOOM
        });
        clusterIndex.load(points.features);
        this.setState({clusterIndex});
    }

    pickupLocationsToGeoJSONFeatures(locations) {
        return {
            type: "FeatureCollection",
            features: locations
                .filter(pickupLocations => pickupLocations.coordinates && pickupLocations.coordinates.latitude && pickupLocations.coordinates.longitude)
                .map((pickupLocation) => ({
                    type: "Feature",
                    properties: {
                        pickupLocation: pickupLocation
                    },
                    geometry: {
                        type: "Point",
                        coordinates: [pickupLocation.coordinates.longitude, pickupLocation.coordinates.latitude]
                    }
                }))
        };
    }

    onDragEnd = (mapProps) => {
        const {center: oldCenter} = this.state;

        const newCenter = {
            lat: mapProps.getCenter().lat(),
            lng: mapProps.getCenter().lng()
        };

        if (!this.isSameCoordinates(oldCenter, newCenter)) {
            const newBounds = mapProps.getBounds();
            const newCenter = mapProps.getCenter();

            const southWest = {
                latitude: newBounds.getSouthWest().lat(),
                longitude: newBounds.getSouthWest().lng()
            };

            const northEast = {
                latitude: newBounds.getNorthEast().lat(),
                longitude: newBounds.getNorthEast().lng()
            };

            const center = {latitude: newCenter.lat(), longitude: newCenter.lng()};

            if (newBounds) {
                this.scheduleFetchLocations(southWest, northEast, center);
            }
        }
    };

    onDrag = () => {
        this.abortFetchLocations();

        this.keyUp = false;
    };

    onKeyUp = () => {
        this.keyUp = true;
    };

    scheduleFetchLocations = (southWest, northEast, center) => {
        const {searchByLocation} = this.props;

        this.abortFetchLocations();

        this.abortController = new AbortController();

        this.timerId = setTimeout(() => {
            searchByLocation(
                southWest,
                northEast,
                center,
                {signal: this.abortController.signal},
                () => this.clearTimer());
        }, MAP_IDLE_TIMEOUT_MS);
    };

    abortFetchLocations = () => {
        this.clearTimer();
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    };

    clearTimer = () => {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
    };

    fetchLocationsScheduled = () => this.timerId !== null;

    render() {
        const {pickupLocations, onSelectPickupLocation, open, simple, isStorePickup, storeLogo, config} = this.props;
        const {center, zoom, googleMapsApiKey, mapLoaded, animationPaused} = this.state;

        const containerClassName = simple ? "window__map-simple" : "window__map";

        let markers = [];
        if (mapLoaded) {
            const clusters = this.getClusterPoints();
            if (clusters && clusters.length > 0) {
                markers = clusters;
            } else if (pickupLocations && pickupLocations.length > 0) {
                markers = pickupLocations.filter(this.hasCoordinates);
            }
        }

        return (
            <div className={containerClassName} onKeyUpCapture={this.onKeyUp}>
                {
                    open && googleMapsApiKey &&
                    <div className="map-container">
                        <GoogleMapReact bootstrapURLKeys={{key: [googleMapsApiKey]}}
                                        center={center}
                                        onTilesLoaded={this.onMapLoaded}
                                        zoom={zoom}
                                        onChange={this.onMapChange}
                                        onDrag={this.onDrag}
                                        onDragEnd={this.onDragEnd}
                                        onGoogleApiLoaded={this.onGoogleApiLoaded}
                                        yesIWantToUseGoogleMapApiInternals>
                            {markers.map(marker => {
                                // If we have clusterPoints, render cluster logic
                                if (marker.properties && marker.properties.cluster) {
                                    const [longitude, latitude] = marker.geometry.coordinates;
                                    const {point_count: pointCount} = marker.properties;
                                    return (
                                        <ClusterMarker
                                            key={`cluster-${marker.id}`}
                                            lat={latitude}
                                            lng={longitude}
                                            pointCount={pointCount}
                                            onClick={this.handleClusterClick(latitude, longitude, marker.id)}
                                        />
                                    );
                                }
                                // Else, render each individual pickupLocation
                                const markerPickupLocation = marker.properties ? marker.properties.pickupLocation : marker;
                                const pickupLocation = this.props.pickupLocations.find(
                                    location => location.code === markerPickupLocation.code
                                ) || markerPickupLocation;
                                return (
                                    <MapMarker key={pickupLocation.code}
                                               lat={pickupLocation.coordinates.latitude}
                                               lng={pickupLocation.coordinates.longitude}
                                               pickupLocation={pickupLocation}
                                               isStorePickup={isStorePickup}
                                               storeLogo={storeLogo}
                                               onSelectPickupLocation={onSelectPickupLocation}
                                               updateMapPosition={this.updateMapPosition}
                                               setTooltipRef={this.setTooltipRef}
                                               config={config}
                                               animationPaused={animationPaused}
                                    />
                                );
                            })}
                        </GoogleMapReact>
                        {/*Render MapControls into Google Maps native controls container using a portal once it's available*/}
                        {this.mapControlsContainer && createPortal(
                            <MapControls paused={animationPaused}
                                         togglePause={this.onTogglePause}
                            />,
                            this.mapControlsContainer
                        )}
                    </div>
                }
            </div>
        );
    }

    handleClusterClick = (latitude, longitude, markerId) => () => {
        const {onSelectPickupLocation} = this.props;
        const {clusterIndex} = this.state;

        onSelectPickupLocation?.(null);
        this.setState({
            center: {lat: latitude, lng: longitude},
            zoom: clusterIndex.getClusterExpansionZoom(markerId)
        });
    };

    hasCoordinates(pickupLocation) {
        return pickupLocation.coordinates && pickupLocation.coordinates.latitude && pickupLocation.coordinates.longitude;
    }

    getClusterPoints() {
        const {zoom, clusterIndex, currentMapProps} = this.state;
        if (clusterIndex && currentMapProps && currentMapProps.bounds) {
            const bounds = currentMapProps.bounds;
            const bbox = [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat];
            return clusterIndex.getClusters(bbox, zoom);
        }
        return [];
    }

    isSameCoordinates = (c1, c2) => {
        if (!c1 || !c2) {
            return false;
        }

        const normalized1 = this.normalizeCoordinates(c1);
        const normalized2 = this.normalizeCoordinates(c2);

        return normalized1.lat === normalized2.lat && normalized1.lng === normalized2.lng;
    };

    normalizeCoordinates = (center) => {
        if (!center?.lat || !center?.lng) {
            return null;
        }

        return {
            lat: Number(center.lat.toFixed(6)),
            lng: Number(center.lng.toFixed(6))
        };
    };
}